/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  findPressedMouseButton,
  mouseButtons
} from "../Utils/EventHandlingUtils";
import { isPointInsideCanvasQuadUnion } from "../Utils/SelectionUtils";
import {
  isTouchEvent,
  isTouchSelectionPresent,
  isTouchSelectionInProgress,
  getTextInSelection,
  getTouchSelPageIndex,
  isThereAnySelection,
  getRichTextInSelection
} from "../selectors/touchSelectors";
import {
  getLeftMostQuad,
  getRightMostQuad
} from "../selectors/pageHighlightsSelectors";
import { isIE, isEdge } from "../Utils/UserAgentUtils";
import { getOS } from "../Utils/DeviceUtils";
import { SELECTION_PRIORITY, pageContentTypes } from "../Constants/constants";
import {
  canvasOperation,
  CaretShortcut,
  clickTypes,
  textManagerMode
} from "../Constants/enums";
import addEventListener from "../Utils/DomUtils";
import { transformQuadFromPDFToView } from "../Utils/PageUtils";
import {
  getCursorQuad,
  getCursorPageIndex
} from "../selectors/cursorSelectors";

const BACKWARD = 0;
const FORWARD = 1;

function isSelectionResponseValid(resp) {
  return (
    resp &&
    resp.data &&
    Array.isArray(resp.data.quads) &&
    resp.data.quads.length > 0
  );
}

function setTouchSelPts({ leftX, leftY, rightX, rightY }, stores) {
  stores.touchSelStore.setSelLeftY(leftY);
  stores.touchSelStore.setSelRightY(rightY);
  stores.touchSelStore.setSelLeftX(leftX);
  stores.touchSelStore.setSelRightX(rightX);
}

function setStartEndPoints(stores, pageIndex) {
  const leftMostQuad = getLeftMostQuad(stores, pageIndex);
  const rightMostQuad = getRightMostQuad(stores, pageIndex);
  if (leftMostQuad && rightMostQuad) {
    const leftX = (leftMostQuad.topLeft.x + leftMostQuad.bottomLeft.x) / 2; // half of (left top x + left bottom x)
    const leftY = (leftMostQuad.topLeft.y + leftMostQuad.bottomLeft.y) / 2; // half of (left top y + left bottom y)
    const rightX = (rightMostQuad.topRight.x + rightMostQuad.bottomRight.x) / 2; // half of (right top x + right bottom x)
    const rightY = (rightMostQuad.topRight.y + rightMostQuad.bottomRight.y) / 2; // half of (right top y + right bottom y)
    setTouchSelPts({ leftX, leftY, rightX, rightY }, stores);
  }
}

class TextManager {
  constructor({ stores, selectionHandler }) {
    this._selectionStartPoint = {
      x: 0,
      y: 0,
      pageNum: 0
    };
    this._currCaretPosition = { x: 0, y: 0, pageNum: 0 };
    this._selectionIsInProgress = false;
    this.newSelection = true; // this is to tell Eureka if the selection end is of new selection or update of existing selection.
    this.counter = 0;
    this.replyCounter = 0;
    this.mouseState = "";
    this.isSelectionEndEventTriggered = false; // needed because docment.mouseup is triggered multiple times
    this.onContextMenu = this.onContextMenu.bind(this);
    this.numOfClicks = clickTypes.NO_CLICK;
    this.lastMouseDownTime = 0;
    this.lastMousePosition = { x: 0, y: 0 };
    this.lastSelectionDirection = undefined;
    this.contextMenuEvent = {};
    this.stores = stores;
    this.selectionHandler = selectionHandler;
    this.copyText = this.copyText.bind(this);
    this.quads = {};
  }

  selectWord(x, y, event, pageIndex) {
    this.stores.pageHighlightsStore.setCanvasOperation(
      canvasOperation.CANVAS_OP_SELECTION
    );
    this.contextMenuEvent = {
      clientX: event.center.x,
      clientY: event.center.y,
      pageIndex,
      target: event.target
    };
    this.stores.touchSelStore.setIsTouchSelectionInProgress(true);
    this.handleLongPress(pageIndex, x, y);
  }

  isSelectionEnd(contentType) {
    return (
      (this.mouseState !== "down" &&
        this.replyCounter === this.counter &&
        !this.isSelectionEndEventTriggered) ||
      (contentType === pageContentTypes.IMAGE &&
        this.replyCounter === this.counter)
    );
  }

  _isOverSelection(pageIndex, x, y) {
    return isPointInsideCanvasQuadUnion(this.stores, pageIndex, x, y);
  }

  _isDoubleOrTripleClick() {
    return (
      this.numOfClicks === clickTypes.DOUBLE_CLICK ||
      this.numOfClicks === clickTypes.TRIPLE_CLICK
    );
  }

  _getSelectionDirection(x) {
    if (x >= this._selectionStartPoint.x) {
      return FORWARD;
    }

    return BACKWARD;
  }

  _shouldShowCursorUpperEdge(
    prevPageIndex,
    currPageIndex,
    prevCursorQuad,
    currentCursorQuad
  ) {
    if (prevPageIndex < currPageIndex) {
      return false;
    } else if (prevPageIndex > currPageIndex) {
      return true;
    } else if (prevCursorQuad[1] < currentCursorQuad[1]) {
      return true;
    } else if (prevCursorQuad[1] > currentCursorQuad[1]) {
      return false;
    }
    return false;
  }

  _handleCaretReply(res, setSelectionStartPoint = false) {
    if (res && res.data && res.data.quads && res.data.quads.length > 0) {
      const prevCursorQuad = getCursorQuad(this.stores);
      const prevCursorPageIndex = getCursorPageIndex(this.stores);
      this.stores.cursorStore.setCursorInfo(res.data.quads[0]);
      this.stores.cursorStore.setIsCursorVisible(true);
      const transformedQuads = transformQuadFromPDFToView(
        res.data.quads[0].quads[0],
        res.data.quads[0].pageNum - 1,
        this.stores
      );
      if (setSelectionStartPoint) {
        const cursorEdge = this._shouldShowCursorUpperEdge(
          prevCursorPageIndex,
          res.data.quads[0].pageNum - 1,
          prevCursorQuad,
          res.data.quads[0].quads[0]
        );
        // Subtracting pageNum by 1 as transformQuadFromPDFToView uses pageIndex
        if (cursorEdge) {
          this._selectionStartPoint = {
            x: transformedQuads[0],
            y: Math.max(transformedQuads[5], transformedQuads[1]),
            pageNum: res.data.quads[0].pageNum
          };
        } else {
          this._selectionStartPoint = {
            x: transformedQuads[0],
            y: Math.min(transformedQuads[5], transformedQuads[1]),
            pageNum: res.data.quads[0].pageNum
          };
        }
      }
      this._currCaretPosition = {
        x: (transformedQuads[0] + transformedQuads[6]) / 2,
        y: (transformedQuads[1] + transformedQuads[7]) / 2,
        pageNum: res.data.quads[0].pageNum
      };
    }
  }

  _handleSelectionReply(resp, replyCounter, contentType) {
    this.replyCounter = replyCounter;
    if (this.stores.cursorStore) {
      this.stores.cursorStore.setIsCursorVisible(false);
    }
    if (isSelectionResponseValid(resp)) {
      const quadsArray = resp.data.quads;
      const quadsArrayMap = {};
      let startPage = -1;
      let endPage = -1;
      quadsArray.forEach(quad => {
        const { pageNum, quads: quadsInPage } = quad;
        const pageIndex = pageNum - 1;
        startPage =
          startPage === -1 ? pageIndex : Math.min(startPage, pageIndex);
        endPage = endPage === -1 ? pageIndex : Math.max(endPage, pageIndex);

        if (!quadsArrayMap[pageIndex]) {
          quadsArrayMap[pageIndex] = [];
        }
        quadsInPage.forEach(currentQuad => {
          const transformedQuad = transformQuadFromPDFToView(
            currentQuad,
            pageIndex,
            this.stores,
            1
          );
          quadsArrayMap[pageIndex].push({ quadPoints: transformedQuad });
        });
      });
      this.stores.touchSelStore.setIsThereAnySelection(true);
      this.stores.touchSelStore.setTextSelectionPageRange({
        startPage,
        endPage
      });

      Object.keys(quadsArrayMap).forEach(pageIndex => {
        this.stores.pageHighlightsStore.updatePageCanvasQuads(
          pageIndex,
          quadsArrayMap[pageIndex],
          true
        );
      });

      // use these quads to convert  into zoomedQuads on the preview side
      this.quads = {
        quadsArray,
        startPage,
        endPage
      };
      if (this.isSelectionEnd(contentType)) {
        this._onSelectionEnd(contentType);
      }
      // since latest selection is drawn here we should clear selection from pages
      // which are now out of selection.
      this.clearOutOfSelectionPages(startPage, endPage);
    }

    return Promise.resolve("");
  }

  // both start and endPage are inclusive
  clearSelectionInPageRange(startPage, endPage) {
    if (
      this.quads &&
      this.quads.startPage >= 0 &&
      this.quads.endPage >= 0 &&
      startPage <= this.quads.startPage &&
      endPage >= this.quads.endPage
    ) {
      this.clearSelection(this.stores);
    } else {
      this.stores.pageHighlightsStore.clearQuadsForPages(
        startPage,
        endPage + 1
      );
    }
  }

  clearSelection(resetStartPoint = true) {
    if (isThereAnySelection(this.stores)) {
      if (resetStartPoint === true)
        this._selectionStartPoint = { x: 0, y: 0, pageNum: 0 };
      this.stores.pageHighlightsStore.clearQuadsForPages(0);
      this.stores.touchSelStore.setTextSelectionPageRange({
        startPage: -1,
        endPage: -1
      });
      this.newSelection = true;
      // trigger clear selection event.. eureka needs this event to hide thier contextual tools.
      // clear text to be copied as well.
      this.stores.touchSelStore.setTextInSelection("");
      this.stores.touchSelStore.setIsThereAnySelection(false);
      this.stores.touchSelStore.setShowGrabbers(false);
      this.stores.touchSelStore.setIsTouchSelectionInProgress(false);
      const args = { stores: this.stores, quads: this.quads.quadsArray };
      this.selectionHandler("selectionclear", args);
    }
  }

  clearCaret(resetStartPoint = true) {
    if (resetStartPoint === true)
      this._selectionStartPoint = { x: 0, y: 0, pageNum: 0 };
    this.stores.cursorStore.setCursorInfo();
    this.stores.cursorStore.setIsCursorVisible(false);
    this._currCaretPosition = { x: 0, y: 0, pageNum: 0 };
  }

  handleHover() {
    this.mouseState = "hover";
  }

  handleTouchMove(pageRect, x1, y1, x2, y2, pageIndex, selectionDir) {
    this._selectionIsInProgress = true;
    this.mouseState = "down";
    this.newSelection = false;
    this._selectionStartPoint = {
      x: x1,
      y: y1,
      pageNum: pageIndex + 1
    };
    this.lastSelectionDirection = selectionDir === 1 ? FORWARD : BACKWARD;

    // autoscroll page view if selection is going beyond the view.
    this.lastMousePosition.x = x2 + pageRect.left;
    this.lastMousePosition.y = y2 + pageRect.top;
    this.selectionHandler("touchMove", {
      x: x2 + pageRect.left,
      y: y2 + pageRect.top,
      pageIndex,
      stores: this.stores
    });
    this.selectTextToPoint(x2, y2, pageIndex, this.lastSelectionDirection);
  }

  keyboardSelection(
    selectionDirection,
    snapToWords,
    jumpTo = CaretShortcut.NONE,
    mode = textManagerMode.SELECTION
  ) {
    if (
      isThereAnySelection(this.stores) ||
      (this._selectionStartPoint.x !== 0 && this._selectionStartPoint.y !== 0)
    ) {
      let currentCount;
      if (mode === textManagerMode.SELECTION) {
        this.newSelection = !isThereAnySelection(this.stores);
        this.isSelectionEndEventTriggered = false;
        this.counter += 1;
        currentCount = this.counter;
      } else {
        this.clearSelection();
      }
      const args = {
        endPoint:
          mode === textManagerMode.CARET ? this._currCaretPosition : undefined,
        selectionDirection,
        snapToWords,
        priority: SELECTION_PRIORITY,
        stores: this.stores,
        getCaret: mode === textManagerMode.CARET,
        jumpTo
      };
      this.selectionHandler("keyboardSelection", args)
        .then(data => {
          let finalQuads;
          if (mode === textManagerMode.SELECTION) {
            this._handleSelectionReply(data, currentCount);
            finalQuads = this.quads.quadsArray;
          } else {
            this._handleCaretReply(data, true);
            finalQuads = data.data.quads;
          }
          this.selectionHandler("keyboardSelectionend", {
            quads: finalQuads,
            selectionPoint: this._selectionStartPoint,
            stores: this.stores,
            getCaret: mode === textManagerMode.CARET
          });
        })
        .catch(() => {});
      return true;
    }
    return false;
  }

  startSelection(
    x,
    y,
    pageIndex,
    mode = textManagerMode.SELECTION,
    contentType
  ) {
    this.counter += 1;
    const selectionDirection = this._getSelectionDirection(x, y);
    const currentPoint = {
      x,
      y,
      pageNum: pageIndex + 1
    };
    const args = {
      startPoint: this._selectionStartPoint,
      endPoint: currentPoint,
      selectionDirection,
      priority: SELECTION_PRIORITY,
      numOfClicks: this.numOfClicks,
      stores: this.stores,
      getCaret: mode === textManagerMode.CARET
    };

    this.selectionHandler("startSelection", args).then(res => {
      // this call simply calls into T4 to mark start of selection. no need to handle reply here.
      if (mode === textManagerMode.CARET) {
        this._handleCaretReply(res);
      }
      if (contentType === pageContentTypes.IMAGE) {
        this._handleSelectionReply(res, this.counter, contentType);
      }
    });
  }

  clearOutOfSelectionPages(startPage, endPage) {
    // clear before selection pages if any
    const minPage = Math.min(startPage, endPage);
    const maxPage = Math.max(startPage, endPage);
    if (minPage > 0)
      this.stores.pageHighlightsStore.clearQuadsForPages(
        Math.max(minPage - 10, 0),
        minPage
      ); // clear last ten pages
    // clear after selection pages. 10 pages
    this.stores.pageHighlightsStore.clearQuadsForPages(
      maxPage + 1,
      maxPage + 11
    ); // clear next 10 pages
  }

  selectTextToPoint(x, y, pageIndex, selectionDir = undefined) {
    this.counter += 1;
    this.isSelectionEndEventTriggered = false;
    const selectionDirection =
      selectionDir !== undefined
        ? selectionDir
        : this._getSelectionDirection(x, y);
    const currentPoint = {
      x,
      y,
      pageNum: pageIndex + 1
    };
    const currentCount = this.counter;
    const args = {
      startPoint: this._selectionStartPoint,
      endPoint: currentPoint,
      selectionDirection,
      priority: SELECTION_PRIORITY,
      numOfClicks: this.numOfClicks,
      stores: this.stores,
      // Whenever select text to point is called, no need to send caret call
      getCaret: false
    };
    return this.selectionHandler("selectTextToPoint", args).then(data => {
      this._handleSelectionReply(data, currentCount);
    });
  }

  handleScroll(x, y, pageIndex) {
    // if mouse state is down then we are in text selection mode, so extend selection.
    if (this.mouseState === "down" && this._selectionIsInProgress === true) {
      if (pageIndex >= 0 && x !== -1 && y !== -1) {
        if (isTouchSelectionInProgress(this.stores) === true)
          this.selectTextToPoint(x, y, pageIndex, this.lastSelectionDirection);
        else this.selectTextToPoint(x, y, pageIndex);
      }
    }
  }

  // starts auto scroll if the xDown, yDown point is near the page view boundary.

  handleDrag(x, y, evt, pageIndex) {
    // don't do anything if mouse state is not down.
    if (this.mouseState !== "down") return Promise.resolve();

    // return if this is a touch event.
    if (isTouchEvent(this.stores) === true) return Promise.resolve();

    // return if selection start point is not set.
    if (this._selectionStartPoint.x === 0 && this._selectionStartPoint.y === 0)
      return Promise.resolve();

    // return if this same point as mouse down point.
    if (x === this._selectionStartPoint.x && y === this._selectionStartPoint.y)
      return Promise.resolve();

    // if current point is not within 4 by 4 box around mouse down point than this is
    // is not a drag event. return in this case.
    if (
      this._selectionIsInProgress === false &&
      !this._isDoubleOrTripleClick() &&
      !(
        Math.abs(this._selectionStartPoint.x - x) < 50 &&
        Math.abs(this._selectionStartPoint.y - y) < 50
      )
    )
      return Promise.resolve();

    this._removeMouseUpHandler = addEventListener(
      document,
      "mouseup",
      this.endSelection.bind(this)
    );
    // if this is start of selection then clear previous selection.
    if (this._selectionIsInProgress === false) this.clearSelection(false);
    this._selectionIsInProgress = true;
    // auto scroll if selection goes beyond page view.
    this.lastMousePosition.x = evt.pageX;
    this.lastMousePosition.y = evt.pageY;
    this.selectionHandler("mouseMove", {
      x: evt.pageX,
      y: evt.pageY,
      stores: this.stores
    });
    return this.selectTextToPoint(x, y, pageIndex);
  }

  handleMouseDown(
    x,
    y,
    evt,
    pgIndex,
    clearPreviousSelection = true,
    mode = textManagerMode.SELECTION,
    contentType
  ) {
    if (isTouchEvent(this.stores) === true) return;
    this.stores.touchSelStore.setIsTouchSelectionInProgress(false);

    this.mouseState = "down";
    this.lastMousePosition.x = evt.pageX;
    this.lastMousePosition.y = evt.pageY;

    const timeDiff = Date.now() - this.lastMouseDownTime;
    if (timeDiff < 500) {
      if (this.numOfClicks === clickTypes.DOUBLE_CLICK)
        this.numOfClicks = clickTypes.TRIPLE_CLICK;
      if (this.numOfClicks === clickTypes.SINGLE_CLICK)
        this.numOfClicks = clickTypes.DOUBLE_CLICK;
    } else {
      this.numOfClicks = clickTypes.SINGLE_CLICK;
    }
    this.lastMouseDownTime = Date.now();

    const buttonPressed = findPressedMouseButton(evt);
    if (isThereAnySelection(this.stores) && clearPreviousSelection) {
      if (
        buttonPressed === mouseButtons.RIGHT ||
        (evt.ctrlKey === true && getOS().name === "Mac OS")
      ) {
        // this may be a ctx menu click over a selection
        if (!this._isOverSelection(pgIndex, x, y) && !evt.shiftKey) {
          this.clearSelection();
        }
      } else if (
        buttonPressed !== mouseButtons.NONE &&
        !(isIE() || isEdge()) &&
        !evt.shiftKey
      ) {
        this.clearSelection();
      }
    }
    if (
      mode === textManagerMode.CARET ||
      (buttonPressed !== mouseButtons.RIGHT &&
        !(evt.ctrlKey === true && getOS().name === "Mac OS"))
    ) {
      if (
        evt.shiftKey &&
        this._selectionStartPoint.x !== 0 &&
        this._selectionStartPoint.y !== 0
      ) {
        this.selectTextToPoint(x, y, pgIndex);
      } else if (pgIndex >= 0 && x !== -1 && y !== -1) {
        this._selectionStartPoint = { x, y, pageNum: pgIndex + 1 };
        this.startSelection(
          this._selectionStartPoint.x,
          this._selectionStartPoint.y,
          pgIndex,
          mode,
          contentType
        );
      } else {
        this._selectionStartPoint = { x: 0, y: 0, pageNum: 0 };
        if (clearPreviousSelection) {
          this.clearSelection();
        }
      }
      if (this._isDoubleOrTripleClick() && !evt.shiftKey) {
        this.stores.pageHighlightsStore.setCanvasOperation(
          canvasOperation.CANVAS_OP_SELECTION
        );
        this.selectTextToPoint(x, y, pgIndex);
      }
    }
  }

  _onSelectionEnd(contentType) {
    const args = {
      stores: this.stores,
      contextMenuEvent: this.contextMenuEvent,
      quads: this.quads.quadsArray,
      contentType
    };
    this.selectionHandler("selectionend", args);
    this.isSelectionEndEventTriggered = true;
    this.counter = 0;
    this.replyCounter = 0;
    //  <TODO> will add here on touch selection after module
    if (isTouchSelectionPresent(this.stores)) {
      setStartEndPoints(this.stores, getTouchSelPageIndex(this.stores));
    }
  }

  endSelection() {
    if (this._removeMouseUpHandler) this._removeMouseUpHandler();
    this._selectionIsInProgress = false;
    this.selectionHandler("mouseUp");
    this.mouseState = "up";
    if (isThereAnySelection(this.stores) && this.isSelectionEnd()) {
      this._onSelectionEnd();
    }
  }

  handleClick(event) {
    const buttonPressed = findPressedMouseButton(event);
    if (
      (isIE() || isEdge()) &&
      !event.shiftKey &&
      !this._isDoubleOrTripleClick() &&
      isTouchEvent(this.stores) !== true &&
      buttonPressed !== mouseButtons.RIGHT
    ) {
      this.clearSelection();
    }
  }

  handleMouseUp() {
    this.endSelection();
  }

  handleTouchEnd() {
    setTimeout(() => {
      this.endSelection();
    }, 0);
  }

  handleTouchDrag(x, y, evt, pageIndex) {
    if (isTouchSelectionInProgress(this.stores) === true) {
      if (evt.cancelable !== false) evt.preventDefault();
      this.selectTextToPoint(x, y, pageIndex);
    }
  }

  handleLongPress(pageIndex, x, y) {
    this.mouseState = "down";
    this.isSelectionEndEventTriggered = false;
    this.counter += 1;
    this._selectionStartPoint = {
      x,
      y,
      pageNum: pageIndex + 1
    };
    const currentCount = this.counter;

    const args = {
      priority: SELECTION_PRIORITY,
      startPoint: this._selectionStartPoint
    };
    this.selectionHandler("selectWord", args)
      .then(resp => {
        this._handleSelectionReply(resp, currentCount);
      })
      .catch(() => {});
  }

  copyText() {
    if (
      isThereAnySelection(this.stores) &&
      getTextInSelection(this.stores) !== ""
    ) {
      const args = {
        text: getTextInSelection(this.stores),
        richText: getRichTextInSelection(this.stores),
        stores: this.stores
      };
      this.selectionHandler("copyText", args);
    }
  }

  getContextMenu(pageIndex, x, y) {
    let menu = [];
    // current canvas operation check is not reliable
    // TODO: either set canvas op reliably or remove it altogether
    if (
      isThereAnySelection(this.stores) &&
      getTextInSelection(this.stores) !== "" &&
      (this._isOverSelection(pageIndex, x, y) ||
        isTouchSelectionPresent(this.stores) === true)
    ) {
      menu = [
        {
          title: "contextMenu.textSelection",
          eventName: "copytext",
          eventHandler: this.copyText,
          args: [],
          position: 10
        },
        {
          title: "separator",
          position: 10
        }
      ];
    }

    return menu;
  }

  onContextMenu({ pageIndex, x, y }) {
    return Promise.resolve(this.getContextMenu(pageIndex, x, y));
  }
}

export default TextManager;
